import { Box, Button, Center, Group, Paper, Text } from "@mantine/core";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";

const DASHBOARD_TOUR_TARGET = "dashboard";
const POLL_MS = 400;

function queryDashboardNav(): Element | null {
  return document.querySelector(`[data-sidebar-tour="${DASHBOARD_TOUR_TARGET}"]`);
}

function readRect(el: Element | null): DOMRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? r : null;
}

export function NewRequestsSpotlight() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, newRequestsSpotlightOpen, acknowledgeNewRequestsSpotlight } = useUserContext();

  const userId = userDetails?.id;
  const [rect, setRect] = useState<DOMRect | null>(null);

  const updateRect = useCallback(() => {
    if (!newRequestsSpotlightOpen) {
      setRect(null);
      return;
    }
    setRect(readRect(queryDashboardNav()));
  }, [newRequestsSpotlightOpen]);

  useLayoutEffect(() => {
    updateRect();
  }, [updateRect]);

  useEffect(() => {
    if (!newRequestsSpotlightOpen) {
      setRect(null);
      return;
    }
    const onWin = () => updateRect();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    const t = window.setInterval(updateRect, POLL_MS);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
      window.clearInterval(t);
    };
  }, [newRequestsSpotlightOpen, updateRect]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get("filter") ?? (params.get("tab") === "sent" ? "sent" : "received");
    const onReceivedTab = location.pathname === "/dashboard" && filter === "received";
    if (onReceivedTab && newRequestsSpotlightOpen) {
      acknowledgeNewRequestsSpotlight();
    }
  }, [location.pathname, location.search, newRequestsSpotlightOpen, acknowledgeNewRequestsSpotlight]);

  if (!newRequestsSpotlightOpen || !userId) {
    return null;
  }

  const handleViewRequests = () => {
    navigate("/dashboard?tab=requests&filter=received");
    acknowledgeNewRequestsSpotlight();
  };

  const cardW = 320;
  const card = (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      maw={cardW}
      w={cardW}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ border: "1px solid rgba(148, 163, 184, 0.35)" }}
    >
      <Text fw={700} size="lg" mb="md" lh={1.35}>
        New collaboration request received
      </Text>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Button variant="subtle" size="compact-sm" color="gray" onClick={acknowledgeNewRequestsSpotlight}>
          Dismiss
        </Button>
        <Button
          size="compact-sm"
          style={{ backgroundColor: "#ffc300", color: "var(--mantine-color-primary-7)" }}
          onClick={handleViewRequests}
        >
          View requests
        </Button>
      </Group>
    </Paper>
  );

  if (!rect) {
    return (
      <Box pos="fixed" inset={0} style={{ zIndex: 10055 }} data-new-requests-spotlight-root>
        <Box
          pos="fixed"
          inset={0}
          style={{ backgroundColor: "rgba(15, 23, 42, 0.74)", zIndex: 10055 }}
          onMouseDown={(e) => e.preventDefault()}
        />
        <Center pos="fixed" inset={0} style={{ zIndex: 10057, pointerEvents: "none" }}>
          <Box style={{ pointerEvents: "auto" }}>{card}</Box>
        </Center>
      </Box>
    );
  }

  const pad = 8;
  const top = rect.top - pad;
  const left = rect.left - pad;
  const width = rect.width + pad * 2;
  const height = rect.height + pad * 2;

  const gap = 16;
  const rightEdge = left + width;
  const canPlaceRight = rightEdge + gap + cardW <= window.innerWidth - gap;
  const canPlaceLeft = left - gap - cardW >= gap;
  const cardLeftPos = canPlaceRight
    ? rightEdge + gap
    : canPlaceLeft
      ? left - gap - cardW
      : Math.min(Math.max(gap, left), window.innerWidth - cardW - gap);
  const cardTopPos = Math.min(Math.max(gap, top), window.innerHeight - 240);

  return (
    <Box pos="fixed" inset={0} style={{ zIndex: 10055 }} data-new-requests-spotlight-root>
      <Box
        pos="fixed"
        style={{
          top,
          left,
          width,
          height,
          borderRadius: 10,
          boxShadow: "0 0 0 3px #ffc300, 0 0 0 9999px rgba(15, 23, 42, 0.74)",
          pointerEvents: "none",
          zIndex: 10055,
        }}
      />
      <Box
        pos="fixed"
        inset={0}
        style={{ zIndex: 10056, cursor: "default" }}
        onMouseDown={(e) => e.preventDefault()}
      />
      <Box
        pos="fixed"
        maw={cardW}
        w={cardW}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          top: cardTopPos,
          left: cardLeftPos,
          zIndex: 10057,
        }}
      >
        {card}
      </Box>
    </Box>
  );
}
